import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import {
  Badge,
  BadgeProps,
  Button,
  Card,
  TextStyle,
  Thumbnail,
  Tooltip,
} from '@shopify/polaris';
import { RefreshMajor } from '@shopify/polaris-icons';
import { ResourcePicker as AppBridgeResourcePicker } from '@shopify/app-bridge/actions';
import { Sectioned } from '@ui/container';
import {
  Checkbox,
  LoadingSkeleton,
  OptionStack,
  ReadOnlyFooter,
  ResourceModal,
} from '@ui';
import { DisplayedSelectedItem } from '@ui/resource-modal/container';
import {
  useCoreClient,
  useFetchCampaigns,
  useFetchCouponCodes,
  useFetchSubscription,
  usePageActions,
  useSaveBar,
} from '@hooks';
import useModal, { useRouteConfirmation } from '@hooks/modal';
import { useShowPageActions } from '@hooks/page-actions';
import { applyIfNotEmpty, toRange } from '@utils/transform';
import { isEqual } from 'lodash';
import {
  Campaign,
  CampaignOfferScope,
  CampaignOfferType,
  CampaignPosOrdersSettings,
  CampaignStatus,
  CampaignTriggerScope,
  CampaignTriggerType,
  CampaignTriggerUpdate,
  CampaignUpdate,
  CouponCode,
  CouponCodeStatus,
  CouponCodeSyncType,
  CouponCodeTriggerExclusion,
  CouponCodeTriggerExclusionTypeEnum,
  FeatureKey,
  Product,
  ProductScope,
  ProductTypeEnum,
  SubscriptionPackage,
} from '@givz/core-client';
import {
  loadingState,
  notificationErrorState,
  notificationSuccessState,
  selectedCampaignSelector,
  selectedCouponCodeListSelector,
  unsavedCouponCodesState,
} from '@states';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useCurrentSession } from '@hooks/auth';
import styled from 'styled-components';
import { LoadingPageType } from '@components/ui/loading-skeleton';
import { useImpersonate } from '@hooks/impersonate';
import { useTriggerAmplitudeEvent } from '@hooks/instrumentation';
import { ExtendedRadioButtonProps } from '@components/ui/radio-stack';
import { useSubscriptionFeatures } from '@hooks/subscription';
import { displayDiscardConfirmModal } from '@utils/modal';
import { CardTitleSmall } from '@components/ui/common';
import { useCoreClientSafe } from '@utils/core-client';
import { getShopifyAdminUrlFromMyShopifyUrl } from '@utils/url';
import { SupportBanner } from '../common';
import ProductScopeStack from './product-scope';
import PosSettings from './pos-settings';

type IncentiveState = {
  amount: string;
  campaignOfferType: CampaignOfferType;
  campaignTriggerType: CampaignTriggerType;
  excludeOrdersWithDiscountCodeEnabled: boolean;
  includeMissingEmail?: boolean;
  minimumPurchase: string;
  oneDonationCreditPerConsumer: boolean;
  percentage: string;
  posOrdersOnly?: boolean;
  productScope?: ProductScope;
  selectedCouponCodeIds: string[];
  selectedProducts: Product[];
  status: CampaignStatus;
};

const couponCodeFilterChoices = [
  { label: 'Active', value: CouponCodeStatus.Active },
  {
    label: 'Scheduled',
    value: CouponCodeStatus.Scheduled,
  },
  { label: 'Expired', value: CouponCodeStatus.Expired },
];

interface CouponCodesItem extends CouponCode {
  key: string;
}

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ThumbnailContainer = styled.div`
  margin-right: 1.25rem;
`;

const ListOptionContainer = styled(FlexContainer)`
  justify-content: space-between;
  width: 100%;
`;

export const CouponCodeLabel = styled.div`
  font-weight: bold;
`;

const BadgeContainer = styled.div`
  .Polaris-Badge {
    text-transform: capitalize;
  }
`;

const DiscountCodesEmptyStateContainer = styled.div`
  color: #6d7175;
  text-align: center;
  padding: 4rem 0;
`;

const DiscountCodesFooterContainer = styled.div`
  color: #6d7175;
  font-size: 12px;
`;

const DiscountCodesEmptyState: FunctionComponent<{
  shopUrl?: string | undefined;
}> = (props) => {
  const { shopUrl } = props;

  return (
    <>
      <DiscountCodesEmptyStateContainer>
        No Discount Codes found. You must create them via the{' '}
        <a
          href={
            shopUrl
              ? `${getShopifyAdminUrlFromMyShopifyUrl(shopUrl)}/discounts`
              : ''
          }
          target="_blank"
          rel="noreferrer"
        >
          Discounts
        </a>
        .
      </DiscountCodesEmptyStateContainer>
    </>
  );
};

const DiscountCodesFooter: FunctionComponent = () => {
  return (
    <DiscountCodesFooterContainer>
      Automatic discounts are not eligible
    </DiscountCodesFooterContainer>
  );
};

export const CouponCodeItem: FunctionComponent<CouponCodesItem> = (props) => {
  const { code, status } = props;
  let displayStatus: BadgeProps['status'] | undefined = 'success';

  if (status === CouponCodeStatus.Scheduled) {
    displayStatus = 'attention';
  }

  if (status === CouponCodeStatus.Expired) {
    displayStatus = undefined;
  }

  return (
    <ListOptionContainer>
      <CouponCodeLabel>{code}</CouponCodeLabel>
      <BadgeContainer>
        <Badge status={displayStatus}>{status.toLowerCase()}</Badge>
      </BadgeContainer>
    </ListOptionContainer>
  );
};

const getDefaultState = (campaign?: Campaign): IncentiveState => {
  let selectedCouponCodeIds: string[] = [];

  const campaignOfferType =
    campaign?.offer.type ?? CampaignOfferType.Percentage;

  if (
    campaign?.trigger.settings &&
    'couponCodes' in campaign.trigger.settings
  ) {
    selectedCouponCodeIds = campaign.trigger.settings.couponCodes.map(
      (couponCode) => couponCode.id.toString(),
    );
  }

  let selectedProducts: Product[] = [];
  let productScope: ProductScope | undefined;

  if (campaign?.trigger.settings && 'products' in campaign.trigger.settings) {
    productScope =
      campaign.trigger.settings.scope ??
      getDefaultProductScope(campaignOfferType);
    selectedProducts = campaign.trigger.settings.products;
  }

  return {
    amount:
      campaign?.offer.type === CampaignOfferType.Amount
        ? campaign.offer.settings.value.toString()
        : '10.00',
    campaignOfferType,
    campaignTriggerType:
      campaign?.trigger.type ?? CampaignTriggerType.AllOrders,
    excludeOrdersWithDiscountCodeEnabled:
      campaign?.trigger.exclusions?.some(
        ({ type, ...exclusion }) =>
          type === CouponCodeTriggerExclusionTypeEnum.CouponCode &&
          (exclusion as CouponCodeTriggerExclusion)
            .excludeOrdersWithDiscountCodeEnabled,
      ) ?? false,
    includeMissingEmail: campaign?.posOrdersSettings?.includeMissingEmail,
    minimumPurchase:
      campaign?.trigger.type === CampaignTriggerType.MinPurchase &&
      campaign.trigger.settings &&
      'value' in campaign.trigger.settings &&
      campaign.trigger.settings.value !== null
        ? campaign.trigger.settings.value.toString()
        : '',
    oneDonationCreditPerConsumer:
      campaign?.oneDonationCreditPerConsumer ?? false,
    percentage:
      campaign?.offer.type === CampaignOfferType.Percentage
        ? campaign.offer.settings.value.toString()
        : '10.0',
    posOrdersOnly: campaign?.posOrdersSettings?.posOrdersOnly,
    productScope,
    selectedCouponCodeIds,
    selectedProducts,
    status: campaign?.status ?? CampaignStatus.Active,
  };
};

export const SelectedProduct: FunctionComponent<Product> = (props) => {
  const { name, thumbnailUrl } = props;

  return (
    <>
      <FlexContainer>
        {thumbnailUrl && (
          <ThumbnailContainer>
            <Thumbnail source={thumbnailUrl} alt="Product image" />
          </ThumbnailContainer>
        )}

        <TextStyle>{name}</TextStyle>
      </FlexContainer>
    </>
  );
};

const getDefaultProductScope = (
  campaignOfferType: CampaignOfferType,
): ProductScope =>
  campaignOfferType === CampaignOfferType.Percentage
    ? ProductScope.PercentageOfSubtotal
    : ProductScope.FixedAmountPerOrder;

const Settings: FunctionComponent = () => {
  useShowPageActions();
  const {
    isFeatureActive,
    isFeatureReadOnly,
    readOnlyHelpText,
    toFeatureButtons,
  } = useSubscriptionFeatures();
  const { amplitudeEventQueue, setAmplitudeEventQueue } =
    useTriggerAmplitudeEvent();
  const selectedCampaign = useRecoilValue(selectedCampaignSelector);
  const { setCampaigns } = useFetchCampaigns();
  const {
    couponCodeSearch,
    couponCodeStatusFilters,
    couponCodes,
    couponCodesFullSyncLoading,
    couponCodesLoading,
    setCouponCodeSearch,
    setCouponCodeStatusFilters,
    setCouponCodeSyncType,
  } = useFetchCouponCodes();
  const [, setUnsavedCouponCodes] = useRecoilState(unsavedCouponCodesState);
  const [modalSelectedCouponCodeIds, setModalSelectedCouponCodeIds] = useState<
    string[]
  >([]);
  const selectedCouponCodeList = useRecoilValue(selectedCouponCodeListSelector);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProductsList, setSelectedProductsList] = useState<
    DisplayedSelectedItem[]
  >([]);
  const coreClient = useCoreClient();
  const coreClientSafe = useCoreClientSafe();
  const { subscriptionsPackages } = useFetchSubscription();
  const session = useCurrentSession();
  const { isLoggedIn: isImpersonateLoggedIn } = useImpersonate();

  const [, setDefaultSubscriptionPackage] = useState<SubscriptionPackage>();
  const [state, setState] = useState<IncentiveState>(
    getDefaultState(selectedCampaign),
  );
  const loading = useRecoilValue(loadingState);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [notificationError, setNotificationError] = useRecoilState(
    notificationErrorState,
  );
  const [notificationSuccess, setNotificationSuccess] = useRecoilState(
    notificationSuccessState,
  );

  useEffect(() => {
    if (subscriptionsPackages) {
      const subscriptionPackage: SubscriptionPackage | undefined =
        subscriptionsPackages?.filter(
          (subscriptionsPackage) => subscriptionsPackage.isDefault,
        )[0];
      if (subscriptionPackage) {
        setDefaultSubscriptionPackage(subscriptionPackage);
      }
    }
  }, [subscriptionsPackages]);

  useEffect(() => {
    setState(getDefaultState(selectedCampaign));
  }, [selectedCampaign]);

  useEffect(() => {
    const updatedSelectedProductsList = state.selectedProducts.map(
      (product) => {
        return {
          id: product.referenceId,
          item: <SelectedProduct {...product} />,
        };
      },
    );

    setSelectedProductsList(updatedSelectedProductsList);
  }, [state.selectedProducts]);

  useRouteConfirmation({
    description: () => (
      <p>If you leave this page, any unsaved changes will be lost.</p>
    ),
    shouldCheck: hasUnsavedChanges,
    title: 'Unsaved changes',
  });

  const modal = useModal();

  const { showSaveBar } = useSaveBar(
    {
      discardAction: {
        onAction: () => displayDiscardConfirmModal(modal, onDiscard),
      },
      message: 'Unsaved Incentive Settings',
      saveAction: {
        onAction: () => onSave(),
      },
    },
    [state, notificationError],
  );

  const { disablePrimaryAction, disableSecondaryAction } = usePageActions(
    {
      primary: {
        content: 'Save',
        disabled: true,
        onAction: () => onSave(),
      },
      secondary: {
        content: 'Discard',
        disabled: true,
        onAction: () => displayDiscardConfirmModal(modal, onDiscard),
      },
    },
    [state, notificationError],
  );

  const updatePageActionsVisibility = useCallback(
    (disabled: boolean) => {
      disablePrimaryAction(disabled);
      disableSecondaryAction(disabled);
    },
    [disablePrimaryAction, disableSecondaryAction],
  );

  const showSaveOptions = useCallback(() => {
    showSaveBar && showSaveBar(true);
    updatePageActionsVisibility(false);
  }, [showSaveBar, updatePageActionsVisibility]);

  const hideSaveOptions = useCallback(() => {
    updatePageActionsVisibility(true);
    showSaveBar && showSaveBar(false);
    setHasUnsavedChanges(false);
  }, [showSaveBar, updatePageActionsVisibility]);

  const onSave = useCallback(
    async (updatedState?: IncentiveState) => {
      if (notificationSuccess) setNotificationSuccess(undefined);
      if (notificationError) setNotificationError(undefined);

      if (!selectedCampaign) {
        setNotificationError('Error saving your incentive');
        return;
      }

      const {
        amount,
        campaignOfferType,
        campaignTriggerType,
        excludeOrdersWithDiscountCodeEnabled,
        includeMissingEmail,
        minimumPurchase,
        oneDonationCreditPerConsumer,
        percentage,
        posOrdersOnly,
        productScope,
        selectedCouponCodeIds,
        selectedProducts,
        status,
      } = updatedState ?? state;

      let posOrdersSettings: CampaignPosOrdersSettings | undefined;

      if (
        campaignTriggerType === CampaignTriggerType.CouponCodes &&
        selectedCouponCodeIds.length === 0
      ) {
        setNotificationError('No discount code selected');
        return;
      }

      if (
        campaignTriggerType === CampaignTriggerType.Products &&
        selectedProducts.length === 0
      ) {
        setNotificationError('No products selected');
        return;
      }

      const exclusions = [
        ...(selectedCampaign.trigger.exclusions ?? []),
      ].filter(
        ({ type }) => type !== CouponCodeTriggerExclusionTypeEnum.CouponCode,
      );

      exclusions.push({
        excludeOrdersWithDiscountCodeEnabled,
        type: CouponCodeTriggerExclusionTypeEnum.CouponCode,
      });

      let trigger: CampaignTriggerUpdate = {
        exclusions,
        type: campaignTriggerType,
      };

      if (campaignTriggerType === CampaignTriggerType.CouponCodes) {
        trigger = {
          ...trigger,
          settings: {
            couponCodeIds: selectedCouponCodeIds.map((selectedCouponCodeId) =>
              parseInt(selectedCouponCodeId),
            ),
          },
        };
      }

      if (campaignTriggerType === CampaignTriggerType.Products) {
        trigger = {
          ...trigger,
          settings: {
            products: selectedProducts,
            scope: productScope,
          },
        };
      }

      if (campaignTriggerType === CampaignTriggerType.MinPurchase) {
        const minimumPurchaseValue = Number(
          parseFloat(minimumPurchase).toFixed(2),
        );

        if (Number.isNaN(minimumPurchaseValue)) {
          setNotificationError('Minimum purchase amount not set');
          return;
        }

        trigger = {
          ...trigger,
          settings: {
            scope: CampaignTriggerScope.Subtotal,
            value: minimumPurchaseValue,
          },
        };
      }

      if (isFeatureActive(FeatureKey.PosOrders)) {
        posOrdersSettings = {
          includeMissingEmail,
          posOrdersOnly,
        };
      }

      const updatedCampaign: CampaignUpdate = {
        offer: {
          settings: {
            scope: CampaignOfferScope.Subtotal,
            value: Number(
              campaignOfferType === CampaignOfferType.Amount
                ? parseFloat(amount).toFixed(2)
                : parseFloat(percentage).toFixed(1),
            ),
          },
          type: campaignOfferType,
        },
        oneDonationCreditPerConsumer,
        posOrdersSettings,
        trigger,
      };

      hideSaveOptions();

      // first, patch the campaign
      const { data, errors } = await coreClientSafe({
        args: {
          campaignUpdate: updatedCampaign,
          gid: selectedCampaign.gid,
        },
        fn: coreClient().Campaigns.update,
      });

      if (errors) {
        setNotificationError(errors);
        showSaveOptions();
      } else {
        setCampaigns(data.campaigns);
        setUnsavedCouponCodes([]);
        setNotificationSuccess('Incentive saved successfully');
        setAmplitudeEventQueue([
          ...amplitudeEventQueue,
          {
            name: 'saved campaign settings',
            params: {
              activate: status === CampaignStatus.Active ? 'on' : 'off',
              trigger: updatedCampaign.trigger,
              type: updatedCampaign.offer?.type,
              value: updatedCampaign.offer?.settings.value,
            },
          },
        ]);
      }
    },
    [
      amplitudeEventQueue,
      coreClient,
      coreClientSafe,
      hideSaveOptions,
      isFeatureActive,
      notificationError,
      notificationSuccess,
      selectedCampaign,
      setAmplitudeEventQueue,
      setCampaigns,
      setNotificationError,
      setNotificationSuccess,
      setUnsavedCouponCodes,
      showSaveOptions,
      state,
    ],
  );

  useEffect(() => {
    if (isEqual(state, getDefaultState(selectedCampaign)) && showSaveBar) {
      hideSaveOptions();
    }
  }, [state, showSaveBar, hideSaveOptions, selectedCampaign]);

  function onDiscard() {
    setState(getDefaultState(selectedCampaign));
    hideSaveOptions();
  }

  const onChange = useCallback(
    (key: keyof IncentiveState) => (value: any) => {
      showSaveOptions();
      updatePageActionsVisibility(false);
      setHasUnsavedChanges(true);

      setState((current) => ({
        ...current,
        [key]: value,
      }));

      const isProductSelected =
        key === 'campaignTriggerType' && value === CampaignTriggerType.Products;

      const isCouponCodeSelected =
        key === 'campaignTriggerType' &&
        value === CampaignTriggerType.CouponCodes;

      if (isCouponCodeSelected) {
        onChange('excludeOrdersWithDiscountCodeEnabled')(false);
      }

      if (key === 'selectedCouponCodeIds') {
        setModalSelectedCouponCodeIds([]);
      }

      if (key === 'campaignOfferType' || isProductSelected) {
        onChange('productScope')(
          getDefaultProductScope(
            isProductSelected ? state.campaignOfferType : value,
          ),
        );
      }

      if (
        key === 'campaignTriggerType' &&
        state.selectedProducts.length !== 0
      ) {
        onChange('selectedProducts')([]);
      }
    },
    [
      showSaveOptions,
      state.campaignOfferType,
      state.selectedProducts.length,
      updatePageActionsVisibility,
    ],
  );

  useEffect(() => {
    if (
      state.campaignTriggerType !== CampaignTriggerType.Products &&
      state.selectedProducts.length > 0
    ) {
      onChange('selectedProducts')([]);
    }
  }, [onChange, state.campaignTriggerType, state.selectedProducts]);

  const onChangeInput = (value: string, option: string) => {
    let parsedOption = option.toLowerCase();

    if (parsedOption === 'min_purchase') {
      parsedOption = 'minimumPurchase';
    }

    onChange(parsedOption as keyof IncentiveState)(value);
  };

  const onToggleCampaignOffer = (_: boolean, id: string) =>
    onChange('campaignOfferType')(id);

  const onToggleCampaignTrigger = (_: boolean, id: string) =>
    onChange('campaignTriggerType')(id);

  const formatInputValue = (key: keyof IncentiveState, value: string) => {
    let formattedValue = value;
    if (key === 'minimumPurchase' || key === 'amount') {
      formattedValue = value === '' ? '1.00' : parseFloat(value).toFixed(2);
    }

    if (key === 'percentage') {
      formattedValue = value === '' ? '0' : parseFloat(value).toFixed(1);
    }

    onChange(key)(formattedValue);
  };

  const handleClearCouponCodeSearchInput = () => {
    setCouponCodeSearch('');
  };

  const handleClearProductSearchInput = () => {
    setProductSearch('');
  };

  const onSelectCouponCode = (selected: string[]) => {
    const currentCampaignSettings = getDefaultState(selectedCampaign);

    const unsavedCouponCodeIds = selected.filter(
      (couponCodeId) =>
        !currentCampaignSettings.selectedCouponCodeIds.includes(couponCodeId),
    );
    const updatedUnsavedCouponCodes = couponCodes.filter((couponCode) =>
      unsavedCouponCodeIds.includes(couponCode.id.toString()),
    );

    setUnsavedCouponCodes(updatedUnsavedCouponCodes);

    setModalSelectedCouponCodeIds(selected);
  };

  const onRemoveCouponCode = (id: string) => {
    const updatedSelectedCouponCodeIds = state.selectedCouponCodeIds.filter(
      (couponCodeId) => couponCodeId !== id,
    );

    onChange('selectedCouponCodeIds')(updatedSelectedCouponCodeIds);
  };

  const onRemoveCouponCodeFilter = (key: string) => {
    const updatedCouponCodeStatusFilters = couponCodeStatusFilters.filter(
      (filter) => filter !== key,
    );

    setCouponCodeStatusFilters(updatedCouponCodeStatusFilters);
  };

  const onSelectProduct = (selected: AppBridgeResourcePicker.SelectPayload) => {
    const parsedSelected = selected.selection.map((selectedProduct) => {
      const referenceId = selectedProduct.id.split('/').pop();

      const productImages = (selectedProduct as AppBridgeResourcePicker.Product)
        .images;

      return {
        name: selectedProduct.title,
        referenceId,
        thumbnailUrl:
          productImages.length > 0 ? productImages[0].originalSrc : undefined,
        type: ProductTypeEnum.Shopify,
      };
    }) as Product[];

    onChange('selectedProducts')(parsedSelected);
  };

  const onRemoveProduct = (id: string) => {
    const updatedSelectedProducts = state.selectedProducts.filter(
      (product) => product.referenceId !== id,
    );

    onChange('selectedProducts')(updatedSelectedProducts);
  };

  const onChangeExcludeOrdersWithDiscountCodeEnabled = (checked: boolean) => {
    onChange('excludeOrdersWithDiscountCodeEnabled')(checked);
  };

  const onChangeOneDonationCreditPerConsumer = (checked: boolean) => {
    onChange('oneDonationCreditPerConsumer')(checked);
  };

  const onChangePosOrdersOnly = (checked: boolean) => {
    onChange('posOrdersOnly')(checked);
  };

  const onChangeIncludeMissingEmail = (checked: boolean) => {
    onChange('includeMissingEmail')(checked);
  };

  const {
    amount,
    campaignOfferType,
    campaignTriggerType,
    excludeOrdersWithDiscountCodeEnabled,
    includeMissingEmail,
    minimumPurchase,
    oneDonationCreditPerConsumer,
    percentage,
    posOrdersOnly,
    productScope,
    selectedCouponCodeIds,
    selectedProducts,
  } = state;

  const qualifyingConditionsOptions: ExtendedRadioButtonProps[] = [
    {
      feature: FeatureKey.CoreBenefits,
      id: CampaignTriggerType.AllOrders,
      label: 'Apply to all orders',
      name: CampaignTriggerType.AllOrders,
    },
    {
      feature: FeatureKey.CoreBenefits,
      id: CampaignTriggerType.MinPurchase,
      inlineInput: {
        onBlur: () => formatInputValue('minimumPurchase', minimumPurchase),
        prefix: '$',
        transform: applyIfNotEmpty(toRange(1, 500)),
        type: 'number',
        value: minimumPurchase,
      },
      label: 'Minimum purchase amount (subtotal)',
      name: CampaignTriggerType.MinPurchase,
    },
    {
      disabled: isImpersonateLoggedIn,
      feature: FeatureKey.TriggerProducts,
      id: CampaignTriggerType.Products,
      label: 'Specific products',
      name: CampaignTriggerType.Products,
    },
    {
      feature: FeatureKey.TriggerDiscountCodes,
      id: CampaignTriggerType.CouponCodes,
      label: 'Specific discount codes',
      name: CampaignTriggerType.CouponCodes,
    },
  ];

  const incentiveTypeOptions: ExtendedRadioButtonProps[] = [
    {
      feature: FeatureKey.CoreBenefits,
      id: CampaignOfferType.Percentage,
      label: 'Percentage of subtotal',
      name: CampaignOfferType.Percentage,
    },
    {
      feature: FeatureKey.CoreBenefits,
      id: CampaignOfferType.Amount,
      label: 'Fixed amount',
      name: CampaignOfferType.Amount,
    },
  ];

  return (
    <LoadingSkeleton
      loading={loading}
      type={LoadingPageType.FULLPAGE}
      pageTitle="Offer Type"
      hideSkeleton={Boolean(selectedCampaign)}
      isSectionedLayout={true}
      cardTitles={['Qualifying Conditions']}
    >
      <Sectioned secondary={<SupportBanner />}>
        <>
          <Card sectioned title={<CardTitleSmall>Offer Type</CardTitleSmall>}>
            <OptionStack
              onToggle={onToggleCampaignOffer}
              onInputChange={onChangeInput}
              buttons={toFeatureButtons({
                buttons: incentiveTypeOptions,
                selectedCampaign,
              })}
              initialOption={campaignOfferType}
              bottomInputs={{
                [CampaignOfferType.Percentage]: {
                  disabled: isFeatureReadOnly(
                    FeatureKey.CoreBenefits,
                    selectedCampaign,
                  ),
                  onBlur: () => formatInputValue('percentage', percentage),
                  suffix: '%',
                  transform: applyIfNotEmpty(toRange(1, 100)),
                  type: 'number',
                  value: percentage,
                },
                [CampaignOfferType.Amount]: {
                  disabled: isFeatureReadOnly(
                    FeatureKey.CoreBenefits,
                    selectedCampaign,
                  ),
                  onBlur: () => formatInputValue('amount', amount),
                  prefix: '$',
                  transform: applyIfNotEmpty(toRange(1, 1000)),
                  type: 'number',
                  value: amount,
                },
              }}
            />
            {isFeatureReadOnly(FeatureKey.CoreBenefits, selectedCampaign) && (
              <ReadOnlyFooter text={readOnlyHelpText(selectedCampaign)} />
            )}
          </Card>

          <Card
            sectioned
            title={<CardTitleSmall>Qualifying Conditions</CardTitleSmall>}
          >
            <OptionStack
              onToggle={onToggleCampaignTrigger}
              onInputChange={onChangeInput}
              buttons={toFeatureButtons({
                buttons: qualifyingConditionsOptions,
                modifyHelpText: true,
                selectedCampaign,
              })}
              initialOption={campaignTriggerType}
              bottomInputs={{
                [CampaignTriggerType.CouponCodes]: (
                  <ResourceModal
                    modalProps={{
                      footer: <DiscountCodesFooter />,
                      onClose: () => {
                        setModalSelectedCouponCodeIds([]);
                      },
                      onOpen: async () => {
                        setCouponCodeSyncType(CouponCodeSyncType.Incremental);
                      },
                      primaryAction: {
                        content: 'Done',
                        onAction: () => {
                          onChange('selectedCouponCodeIds')(
                            modalSelectedCouponCodeIds,
                          );
                          handleClearCouponCodeSearchInput();
                        },
                      },
                      title: 'Discount Codes',
                    }}
                    useShopifyResourcePicker={false}
                    items={couponCodes}
                    displayInline
                    onRemoveItem={onRemoveCouponCode}
                    renderItem={CouponCodeItem}
                    filters={{
                      children: (
                        <Tooltip
                          content={
                            couponCodesFullSyncLoading
                              ? 'Syncing Discount Codes... This may take a few minutes for large stores'
                              : 'Re-Sync all Discount Codes from Shopify'
                          }
                        >
                          <Button
                            icon={RefreshMajor}
                            loading={couponCodesFullSyncLoading}
                            onClick={() =>
                              setCouponCodeSyncType(CouponCodeSyncType.Full)
                            }
                          />
                        </Tooltip>
                      ),
                      choices: couponCodeFilterChoices,
                      disabled: couponCodesFullSyncLoading,
                      inputId: 'couponCodeSearch',
                      label: 'Status',
                      onChangeFilter: (selected: string[]) => {
                        setCouponCodeStatusFilters(
                          selected as CouponCodeStatus[],
                        );
                      },
                      onQueryChange: (queryValue: string) => {
                        setCouponCodeSearch(queryValue);
                      },
                      onQueryClear: () => {
                        handleClearCouponCodeSearchInput();
                      },
                      queryPlaceholder: 'Search discount codes',
                      queryValue: couponCodeSearch,
                      selected: couponCodeStatusFilters,
                      selectedFilterTags: couponCodeStatusFilters.map(
                        (filter) => {
                          return {
                            key: filter,
                            label: filter,
                            onRemove: onRemoveCouponCodeFilter,
                          };
                        },
                      ),
                    }}
                    selectedItems={selectedCouponCodeIds}
                    displayedSelectedItems={selectedCouponCodeList.filter(
                      (selectedCouponCode) =>
                        selectedCouponCodeIds.includes(selectedCouponCode.id),
                    )}
                    onSelectionChange={onSelectCouponCode}
                    emptyState={
                      <DiscountCodesEmptyState shopUrl={session?.shop} />
                    }
                    loading={couponCodesLoading || couponCodesFullSyncLoading}
                  />
                ),
                [CampaignTriggerType.Products]: !isImpersonateLoggedIn && (
                  <ResourceModal
                    useShopifyResourcePicker={true}
                    shopifyResourceType="Product"
                    filters={{
                      inputId: 'productSearch',
                      onQueryChange: (queryValue: string) => {
                        setProductSearch(queryValue);
                      },
                      onQueryClear: () => {
                        handleClearProductSearchInput();
                      },
                      queryPlaceholder: 'Search products',
                      queryValue: productSearch,
                    }}
                    items={[]}
                    displayInline
                    onRemoveItem={onRemoveProduct}
                    selectedItems={selectedProducts.map(
                      (product) => product.referenceId,
                    )}
                    displayedSelectedItems={selectedProductsList}
                    onResourcePickerSelection={onSelectProduct}
                  />
                ),
              }}
            />
            {isFeatureReadOnly(FeatureKey.CoreBenefits, selectedCampaign) && (
              <ReadOnlyFooter text={readOnlyHelpText(selectedCampaign)} />
            )}
          </Card>
          {isFeatureActive(FeatureKey.PosOrders) && (
            <PosSettings
              includeMissingEmail={includeMissingEmail}
              onChangeIncludeMissingEmail={onChangeIncludeMissingEmail}
              onChangePosOrdersOnly={onChangePosOrdersOnly}
              posOrdersOnly={posOrdersOnly}
            />
          )}

          <Card sectioned title={<CardTitleSmall>Exclusions</CardTitleSmall>}>
            <Checkbox
              label="Disqualify orders that include a discount code"
              id="excludeOrdersWithDiscountCodeEnabled"
              checked={excludeOrdersWithDiscountCodeEnabled}
              onChange={onChangeExcludeOrdersWithDiscountCodeEnabled}
              disabled={campaignTriggerType === CampaignTriggerType.CouponCodes}
            />

            {isFeatureActive(FeatureKey.OneDonationCreditPerConsumer) && (
              <Checkbox
                label="Disqualify orders where the customer already received a Givz donation credit"
                disabled={isFeatureReadOnly(
                  FeatureKey.OneDonationCreditPerConsumer,
                )}
                id="oneDonationCreditPerConsumer"
                checked={oneDonationCreditPerConsumer}
                onChange={onChangeOneDonationCreditPerConsumer}
              />
            )}
          </Card>

          {campaignTriggerType === CampaignTriggerType.Products && (
            <Card
              sectioned
              title={<CardTitleSmall>Advanced Options</CardTitleSmall>}
            >
              <ProductScopeStack
                initialState={productScope}
                offerType={campaignOfferType}
                onChange={onChange('productScope')}
              />
            </Card>
          )}
        </>
      </Sectioned>
    </LoadingSkeleton>
  );
};

export default Settings;
